/* Team Availability, Leave Analytics and Team Calendar are commented out below but kept
   for later; their helpers stay defined so they can be restored without re-adding code. */
/* eslint-disable no-unused-vars */
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, useOutletContext } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardList,
  Wallet,
  CheckCircle,
  BarChart3,
  Clock,
  CheckCheck,
  XCircle,
  Sparkles,
  Plus,
  CalendarDays,
  CalendarOff,
  Users,
  Download,
  BookOpen,
  Plane,
  Sun,
  Heart,
  Briefcase,
  ChevronRight,
  ArrowUpRight,
  PartyPopper,
  Flag,
  Bell,
  Check,
  X,
  Eye,
  TrendingUp,
  Activity,
  Megaphone,
  MapPin,
  Home,
  Coffee,
} from "lucide-react";
import BrixigoPageLoader from "../../common/BrixigoPageLoader";
import { AuthContext } from "../../../Moduels/Context/AuthContext";
import { hasRole, R } from "../../../rbac/access";
import {
  approveLeaveRequest,

  fetchLeaveBalances,
  fetchLeaveReportSummary,
  fetchLeaveRequests,
  fetchMyLeaveQueue,
  fetchOrgHolidays,
} from "../../../Moduels/leave/leaveApi";
import {
  isEmployeeSelfService,
  resolveEmployeeIdFromProfile,
} from "../../common/employeeUtils";
import { useLeaveOrg } from "./useLeaveOrg";
import LvLeaveApply from "./LvLeaveApply";
import LvLeaveAssign from "./LvLeaveAssign";
import LvLeaveApproval from "./LvLeaveApproval";
import LvLeaveBalances from "./LvLeaveBalances";
import LvLeaveAnalytics from "./LvLeaveAnalytics";
import { LeaveKpi, LeaveKpiGrid, LeaveQuickAction } from "./leaveTheme";
import "../../../styles/leave-page.css";
import "../../../styles/attendance-mvp.css";
import "../../../styles/leave-attendance-theme.css";

/* ============================================================
   THEME TOKENS
============================================================ */

const T = {
  primary: "#2563EB",
  secondary: "#06B6D4",
  success: "#10B981",
  warning: "#F59E0B",
  danger: "#EF4444",
  purple: "#7C3AED",
};

/* ============================================================
   UTILITIES
============================================================ */

const fmtDateLong = (d) =>
  new Date(d).toLocaleDateString(undefined, {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });


const fmtMonthDay = (iso) => {
  if (!iso) return "—";
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

const daysFromToday = (iso) => {
  if (!iso) return null;
  const target = new Date(`${iso}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
};

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return "yesterday";
  if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

const initials = (name = "") =>
  String(name)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase() || "?";

const avatarTone = (seed = "") => {
  const tones = [
    "from-blue-500 to-indigo-600",
    "from-emerald-500 to-teal-600",
    "from-amber-500 to-orange-600",
    "from-rose-500 to-pink-600",
    "from-violet-500 to-fuchsia-600",
    "from-cyan-500 to-sky-600",
  ];
  let n = 0;
  for (let i = 0; i < seed.length; i += 1) n = (n + seed.charCodeAt(i)) % tones.length;
  return tones[n];
};

const normalizeBalance = (row) => {
  const allocated =
    Number(row?.allocated ?? row?.entitled ?? row?.opening_balance ?? 0) || 0;
  const used = Number(row?.used ?? row?.consumed ?? 0) || 0;
  const balance =
    row?.balance != null
      ? Number(row.balance)
      : Math.max(0, allocated - used);
  return { allocated, used, balance };
};

const LEAVE_TYPE_STYLE = (codeOrName = "") => {
  const k = String(codeOrName).toLowerCase();
  if (k.includes("sick") || k.includes("sl"))
    return { icon: Heart, gradient: "from-rose-500 to-pink-600", soft: "bg-rose-50", text: "text-rose-700", ring: "ring-rose-200", color: T.danger };
  if (k.includes("annual") || k.includes("earned") || k.includes("el") || k.includes("pl") || k.includes("privileg"))
    return { icon: Plane, gradient: "from-blue-500 to-indigo-600", soft: "bg-blue-50", text: "text-blue-700", ring: "ring-blue-200", color: T.primary };
  if (k.includes("casual") || k.includes("cl"))
    return { icon: Coffee, gradient: "from-amber-500 to-orange-600", soft: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200", color: T.warning };
  if (k.includes("comp") || k.includes("co"))
    return { icon: Briefcase, gradient: "from-violet-500 to-fuchsia-600", soft: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-200", color: T.purple };
  if (k.includes("maternity") || k.includes("paternity") || k.includes("parental"))
    return { icon: Heart, gradient: "from-fuchsia-500 to-pink-600", soft: "bg-fuchsia-50", text: "text-fuchsia-700", ring: "ring-fuchsia-200", color: "#D946EF" };
  if (k.includes("wfh") || k.includes("remote"))
    return { icon: Home, gradient: "from-cyan-500 to-sky-600", soft: "bg-cyan-50", text: "text-cyan-700", ring: "ring-cyan-200", color: T.secondary };
  return { icon: Sun, gradient: "from-emerald-500 to-teal-600", soft: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200", color: T.success };
};

/* ============================================================
   PRIMITIVE COMPONENTS
============================================================ */

/* Attendance-style section title. */
function SectionHeader({ title, sub, action }) {
  return (
    <div className="lv-att-section-head">
      <div className="min-w-0">
        <h2 className="lv-att-section-title">{title}</h2>
        {sub ? <p className="lv-att-section-sub">{sub}</p> : null}
      </div>
      {action}
    </div>
  );
}

/* Attendance-style card header: blue title with icon, optional right slot. */
function CardHead({ icon: Icon, title, right }) {
  return (
    <div className="lv-att-card-head">
      <span className="lv-att-card-head-title">
        {Icon ? <Icon size={16} /> : null}
        {title}
      </span>
      {right}
    </div>
  );
}

/* Columns per row so Quick Action rows come out even (6 → 3+3, 5 → 3+2, 7 → 4+3). */
function quickActionCols(count) {
  // Rows of three compact tiles (e.g. six tiles → 3 + 3).
  return Math.min(Math.max(count, 1), 3);
}

function Card({ className = "", children, padded = true }) {
  return <div className={`lv-att-card ${padded ? "p-4" : ""} ${className}`}>{children}</div>;
}

function Pill({ tone = "slate", children, icon: Icon }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    cyan: "bg-cyan-50 text-cyan-700 border-cyan-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold ${tones[tone] || tones.slate}`}>
      {Icon ? <Icon size={11} /> : null}
      {children}
    </span>
  );
}

function CircularProgress({ value = 0, total = 1, size = 120, stroke = 10, color = T.primary, label, sublabel }) {
  const safeTotal = Math.max(Number(total) || 0, 1);
  const safeValue = Math.max(0, Math.min(Number(value) || 0, safeTotal));
  const pct = safeValue / safeTotal;
  const radius = (size - stroke) / 2;
  const c = 2 * Math.PI * radius;
  const dash = c * pct;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="#E5E7EB" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          fill="none"
          style={{ transition: "stroke-dasharray 800ms cubic-bezier(0.22,1,0.36,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <div className="text-[22px] font-extrabold text-slate-900 leading-none">{label}</div>
        {sublabel ? <div className="text-[11px] text-slate-500 mt-1 font-medium">{sublabel}</div> : null}
      </div>
    </div>
  );
}

function MiniBars({ values = [], color = T.primary, height = 56, gap = 4 }) {
  if (!values.length) return null;
  const max = Math.max(...values, 1);
  return (
    <div className="flex items-end gap-1 h-full" style={{ height, columnGap: gap }}>
      {values.map((v, i) => {
        const h = Math.max(4, (v / max) * height);
        return (
          <div
            key={i}
            className="flex-1 rounded-md transition-all"
            style={{
              height: h,
              background: `linear-gradient(180deg, ${color} 0%, ${color}88 100%)`,
            }}
            title={String(v)}
          />
        );
      })}
    </div>
  );
}

function Donut({ segments = [], size = 160, stroke = 22 }) {
  const total = segments.reduce((a, b) => a + (b.value || 0), 0) || 1;
  const radius = (size - stroke) / 2;
  const c = 2 * Math.PI * radius;
  let cursor = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={radius} stroke="#F1F5F9" strokeWidth={stroke} fill="none" />
      {segments.map((seg, i) => {
        const len = (seg.value / total) * c;
        const dasharray = `${len} ${c - len}`;
        const dashoffset = -cursor;
        cursor += len;
        return (
          <circle
            key={i}
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={seg.color}
            strokeWidth={stroke}
            strokeDasharray={dasharray}
            strokeDashoffset={dashoffset}
            fill="none"
            strokeLinecap="butt"
          />
        );
      })}
    </svg>
  );
}

function Avatar({ name = "", size = 36, gradient }) {
  const tone = gradient || avatarTone(name);
  return (
    <div
      className={`shrink-0 rounded-full bg-gradient-to-br ${tone} text-white font-bold flex items-center justify-center shadow-sm ring-2 ring-white`}
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initials(name)}
    </div>
  );
}

function EmptyState({ icon: Icon = Sparkles, title, hint, action }) {
  return (
    <div className="py-10 px-4 text-center">
      <div className="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200 text-slate-400 flex items-center justify-center mb-3">
        <Icon size={22} />
      </div>
      <p className="text-sm font-semibold text-slate-800">{title}</p>
      {hint ? <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto">{hint}</p> : null}
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  );
}

/* ============================================================
   FEATURE COMPONENTS
============================================================ */

function BalanceCard({ type, allocated, used, balance }) {
  const style = LEAVE_TYPE_STYLE(type);
  const Icon = style.icon;
  const safeTotal = allocated || balance + used || 1;
  const pct = Math.min(100, Math.round((used / safeTotal) * 100));
  return (
    <Card padded={false} className="overflow-hidden group">
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${style.gradient} text-white flex items-center justify-center shadow-md group-hover:scale-110 transition`}>
            <Icon size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] font-bold text-slate-800 leading-tight">{type}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">{pct}% utilized</p>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-center">
          <CircularProgress
            value={balance}
            total={safeTotal}
            size={130}
            stroke={11}
            color={style.color}
            label={
              <span className="tabular-nums">
                {Number(balance).toFixed(0)}
                <span className="text-slate-400 font-bold text-base">/{Number(safeTotal).toFixed(0)}</span>
              </span>
            }
            sublabel="days left"
          />
        </div>
        <div className="mt-4 grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Total</p>
            <p className="text-[13px] font-extrabold text-slate-800 tabular-nums">{Number(safeTotal).toFixed(0)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Used</p>
            <p className="text-[13px] font-extrabold text-amber-600 tabular-nums">{Number(used).toFixed(0)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Left</p>
            <p className="text-[13px] font-extrabold text-emerald-600 tabular-nums">{Number(balance).toFixed(0)}</p>
          </div>
        </div>
      </div>
    </Card>
  );
}

function TimelineRow({ type, dates, status, days, reason }) {
  const style = LEAVE_TYPE_STYLE(type);
  const Icon = style.icon;
  const statusTone =
    status === "approved" ? "emerald" : status === "rejected" ? "rose" : status === "cancelled" ? "slate" : "amber";
  return (
    <div className="flex gap-3 py-3 px-3 -mx-3 rounded-xl hover:bg-slate-50 transition cursor-pointer group">
      <div className={`shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br ${style.gradient} text-white flex items-center justify-center shadow-sm`}>
        <Icon size={15} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] font-bold text-slate-900 truncate">{type}</p>
          <Pill tone={statusTone}>{status}</Pill>
        </div>
        <p className="text-[12px] text-slate-600 mt-0.5 flex items-center gap-1.5">
          <CalendarDays size={11} className="text-slate-400" />
          {dates}
          <span className="text-slate-300">·</span>
          <span className="font-semibold text-slate-700">{days} day{days === 1 ? "" : "s"}</span>
        </p>
        {reason ? (
          <p className="text-[11px] text-slate-500 mt-0.5 truncate group-hover:text-slate-600">{reason}</p>
        ) : null}
      </div>
    </div>
  );
}

function ApprovalCard({ req, onApprove, onReject, busy }) {
  const style = LEAVE_TYPE_STYLE(req.leave_type_name || req.leave_type_code);
  const name = req.employee_name || req.employee_code || "Employee";
  const dept = req.department_name || req.designation_name || "—";
  const days = Number(req.total_days || req.days || 0);
  return (
    <div className="bg-white rounded-2xl border border-slate-200/80 p-4 hover:border-blue-300 hover:shadow-[0_8px_24px_-14px_rgba(37,99,235,0.25)] transition-all">
      <div className="flex items-start gap-3">
        <Avatar name={name} size={40} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[14px] font-bold text-slate-900 truncate">{name}</p>
            <Pill tone="slate">{dept}</Pill>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[12px] text-slate-600">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${style.soft} ${style.text} font-semibold`}>
              <style.icon size={10} />
              {req.leave_type_name || req.leave_type_code || "Leave"}
            </span>
            <span className="inline-flex items-center gap-1 text-slate-500">
              <CalendarDays size={11} />
              {fmtMonthDay(req.start_date)} — {fmtMonthDay(req.end_date)}
            </span>
            <span className="font-bold text-slate-800">{days}d</span>
          </div>
          {req.reason ? (
            <p className="text-[12px] text-slate-600 mt-2 line-clamp-2 italic">"{req.reason}"</p>
          ) : null}
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => onApprove?.(req)}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-bold text-white bg-gradient-to-r from-emerald-500 to-green-600 shadow-sm hover:shadow-md hover:-translate-y-0.5 disabled:opacity-50 transition"
        >
          <Check size={13} />
          Approve
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onReject?.(req)}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-bold text-rose-700 bg-rose-50 border border-rose-200 hover:bg-rose-100 disabled:opacity-50 transition"
        >
          <X size={13} />
          Reject
        </button>
        <button
          type="button"
          disabled={busy}
          className="px-2.5 py-2 rounded-xl text-[12px] font-semibold text-slate-600 hover:bg-slate-100 transition"
          title="View details"
        >
          <Eye size={13} />
        </button>
      </div>
    </div>
  );
}

function HolidayChip({ name, date, isOptional, idx = 0 }) {
  const tones = [
    "from-rose-500 to-pink-600",
    "from-blue-500 to-indigo-600",
    "from-amber-500 to-orange-600",
    "from-emerald-500 to-teal-600",
    "from-violet-500 to-fuchsia-600",
    "from-cyan-500 to-sky-600",
  ];
  const tone = tones[idx % tones.length];
  const days = daysFromToday(date);
  return (
    <div className="shrink-0 w-44 rounded-2xl border border-slate-200/80 bg-white p-3 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 group">
      <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${tone} text-white flex items-center justify-center shadow-sm mb-2.5`}>
        <PartyPopper size={15} />
      </div>
      <p className="text-[12px] uppercase tracking-wide font-bold text-slate-500">{fmtMonthDay(date)}</p>
      <p className="text-[14px] font-bold text-slate-900 leading-tight mt-0.5 line-clamp-2">{name}</p>
      <div className="mt-2 flex items-center justify-between">
        {isOptional ? (
          <Pill tone="amber" icon={Sparkles}>Optional</Pill>
        ) : (
          <Pill tone="blue" icon={Flag}>Public</Pill>
        )}
        <span className="text-[10px] font-bold text-slate-500">
          {days === 0 ? "Today" : days === 1 ? "Tomorrow" : `${days}d`}
        </span>
      </div>
    </div>
  );
}

function ActivityRow({ icon: Icon, color, name, action, target, when }) {
  return (
    <div className="flex items-start gap-3 py-2.5">
      <div className="relative">
        <Avatar name={name} size={32} />
        <span
          className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-white shadow"
          style={{ background: color }}
        >
          <Icon size={10} />
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[12.5px] text-slate-700 leading-snug">
          <span className="font-bold text-slate-900">{name}</span>{" "}
          <span className="text-slate-500">{action}</span>{" "}
          <span className="font-semibold text-slate-800">{target}</span>
        </p>
        <p className="text-[11px] text-slate-400 mt-0.5">{when}</p>
      </div>
    </div>
  );
}


/* ============================================================
   PAGE LAYOUT
============================================================ */

const ALL_SECTIONS = [
  { id: "dashboard", label: "Dashboard", icon: LayoutDashboard, roles: [R.ADMIN, R.HR, R.MANAGER, R.EMPLOYEE], pageKey: null },
  { id: "apply", label: "Apply Leave", icon: ClipboardList, roles: [R.HR, R.MANAGER, R.EMPLOYEE], pageKey: "lv_apply" },
  // Super Admin assigns leave for another employee. Other roles apply for themselves.
  { id: "assign", label: "Assign Leave", icon: Users, roles: [R.ADMIN], pageKey: "lv_approval" },
  { id: "balances", label: "Balances", icon: Wallet, roles: [R.ADMIN, R.HR, R.MANAGER, R.EMPLOYEE], pageKey: "lv_balance" },
  { id: "comp-off", label: "Comp Off", icon: CalendarOff, roles: [R.ADMIN, R.HR, R.MANAGER, R.EMPLOYEE], pageKey: "lv_comp_off" },
  { id: "approval", label: "Approve", icon: CheckCircle, roles: [R.ADMIN, R.HR, R.MANAGER], pageKey: "lv_approval" },
  { id: "analytics", label: "Analytics", icon: BarChart3, roles: [R.ADMIN, R.HR], pageKey: "lv_analytics" },
];

function sectionAllowed(section, role, pageKeys) {
  if (!hasRole(role, section.roles)) return false;
  if (!section.pageKey) return true;
  if (section.id === "comp-off" && hasRole(role, [R.ADMIN, R.HR, R.MANAGER, R.EMPLOYEE])) return true;
  if (!pageKeys?.length) return true;
  return pageKeys.includes(section.pageKey);
}

/* ============================================================
   DASHBOARD OVERVIEW (REDESIGNED)
============================================================ */

function DashboardOverview({ report, actionableCount, sections, onNavigate }) {
  const { orgId } = useLeaveOrg();
  const { user, role, principalType } = useContext(AuthContext);
  const selfService = isEmployeeSelfService(user, role, principalType);
  const employeeId = useMemo(() => resolveEmployeeIdFromProfile(user) || "", [user]);
  const year = new Date().getFullYear();

  const [balances, setBalances] = useState([]);
  const [myQueue, setMyQueue] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [recentRequests, setRecentRequests] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [approvalBusy, setApprovalBusy] = useState(false);
  const [comingSoonToast, setComingSoonToast] = useState(null);

  const showComingSoon = (feature) => {
    setComingSoonToast(feature);
    setTimeout(() => setComingSoonToast(null), 3000);
  };

  const isApprover = hasRole(role, [R.ADMIN, R.HR, R.MANAGER]);
  // "Apply" follows the section's role rules (every role applies for its own linked employee record).
  const canApply = (sections || []).some((s) => s.id === "apply");

  // Balances
  useEffect(() => {
    if (!orgId || !employeeId) return;
    fetchLeaveBalances({ organisation_id: orgId, employee_id: employeeId, year })
      .then((d) => setBalances(Array.isArray(d) ? d : d?.balances || []))
      .catch(() => setBalances([]));
  }, [orgId, employeeId, year]);

  // My queue (upcoming + recent requests for me)
  useEffect(() => {
    if (!orgId) return;
    fetchMyLeaveQueue(orgId, employeeId ? { employee_id: employeeId } : {})
      .then((rows) => setMyQueue(rows || []))
      .catch(() => setMyQueue([]));
  }, [orgId, employeeId]);

  // Pending approvals
  useEffect(() => {
    if (!orgId || !isApprover) return;
    fetchLeaveRequests({ organisation_id: orgId, actionable_only: true })
      .then((rows) => setPendingApprovals(Array.isArray(rows) ? rows : rows?.items || []))
      .catch(() => setPendingApprovals([]));
  }, [orgId, isApprover]);

  // Recent requests (all statuses — powers the activity feed)
  useEffect(() => {
    if (!orgId) return;
    fetchLeaveRequests({ organisation_id: orgId })
      .then((rows) => setRecentRequests(Array.isArray(rows) ? rows : rows?.items || []))
      .catch(() => setRecentRequests([]));
  }, [orgId]);

  // Holidays
  useEffect(() => {
    if (!orgId) return;
    const today = new Date();
    const from = today.toISOString().slice(0, 10);
    const end = new Date(today.getFullYear(), 11, 31).toISOString().slice(0, 10);
    fetchOrgHolidays({ organisation_id: orgId, from_date: from, to_date: end })
      .then((rows) => setHolidays(Array.isArray(rows) ? rows : []))
      .catch(() => setHolidays([]));
  }, [orgId]);

  /* Derived */
  const todayIso = new Date().toISOString().slice(0, 10);

  const upcomingMine = useMemo(
    () =>
      (myQueue || [])
        .filter((r) => String(r.end_date || r.start_date) >= todayIso)
        .sort((a, b) => String(a.start_date).localeCompare(String(b.start_date)))
        .slice(0, 5),
    [myQueue, todayIso]
  );

  const onLeaveToday = useMemo(
    () =>
      (pendingApprovals || [])
        .concat(myQueue || [])
        .filter((r) => {
          const s = (r.status || "").toLowerCase();
          if (s !== "approved") return false;
          return r.start_date <= todayIso && r.end_date >= todayIso;
        }),
    [pendingApprovals, myQueue, todayIso]
  );

  const totalAllocated = balances.reduce((s, b) => s + normalizeBalance(b).allocated, 0);
  const totalUsed = balances.reduce((s, b) => s + normalizeBalance(b).used, 0);
  const utilization = totalAllocated ? Math.round((totalUsed / totalAllocated) * 100) : 0;

  const totalApproved = Number(report?.approved) || 0;
  const totalRejected = Number(report?.rejected) || 0;
  const totalPending = Number(report?.pending) || 0;

  /* Holiday upcoming */
  const upcomingHolidays = useMemo(
    () =>
      (holidays || [])
        .filter((h) => String(h.holiday_date || h.date || "") >= todayIso)
        .sort((a, b) => String(a.holiday_date || a.date).localeCompare(String(b.holiday_date || b.date)))
        .slice(0, 8),
    [holidays, todayIso]
  );

  /* Distribution by leave type for donut */
  const typeDistribution = useMemo(() => {
    const segs = balances
      .filter((b) => normalizeBalance(b).used > 0)
      .map((b) => {
        const style = LEAVE_TYPE_STYLE(b.leave_type_name || b.leave_type_code);
        return {
          label: b.leave_type_name || b.leave_type_code || "Leave",
          value: normalizeBalance(b).used,
          color: style.color,
        };
      });
    if (!segs.length) {
      return [
        { label: "Annual", value: 5, color: T.primary },
        { label: "Sick", value: 2, color: T.danger },
        { label: "Casual", value: 3, color: T.warning },
        { label: "Comp", value: 1, color: T.purple },
      ];
    }
    return segs;
  }, [balances]);

  /* Monthly trend approximated from queue */
  const monthlyTrend = useMemo(() => {
    const buckets = Array(12).fill(0);
    for (const r of myQueue || []) {
      const m = Number(String(r.start_date || "").slice(5, 7)) - 1;
      if (m >= 0 && m < 12) buckets[m] += Number(r.total_days || r.days || 1);
    }
    const empty = buckets.every((v) => !v);
    return empty ? [3, 4, 6, 5, 7, 9, 8, 11, 7, 6, 9, 5] : buckets;
  }, [myQueue]);

  /* Dynamic activity feed from real leave requests */
  const recentActivity = useMemo(() => {
    const STATUS_MAP = {
      approved: { icon: Check, color: T.success, action: "approved" },
      rejected: { icon: X, color: T.danger, action: "rejected" },
      pending: { icon: Clock, color: T.warning, action: "applied for" },
      escalated: { icon: ArrowUpRight, color: T.purple, action: "escalated" },
      cancelled: { icon: XCircle, color: T.danger, action: "cancelled" },
    };
    const rows = (recentRequests || [])
      .slice()
      .sort((a, b) => {
        const da = a.updated_at || a.created_at || "";
        const db = b.updated_at || b.created_at || "";
        return db.localeCompare(da);
      })
      .slice(0, 6);
    return rows.map((r) => {
      const st = String(r.status || "pending").toLowerCase();
      const meta = STATUS_MAP[st] || STATUS_MAP.pending;
      const name = r.employee_name || r.employee_code || "Employee";
      const days = r.total_days || r.days || 1;
      const typeName = r.leave_type_name || r.leave_type_code || "Leave";
      const target = st === "approved" || st === "rejected"
        ? `${days} day${days !== 1 ? "s" : ""} of ${typeName}`
        : `${days} day${days !== 1 ? "s" : ""} of ${typeName}`;
      const action = st === "pending" ? "applied for" : meta.action;
      const when = timeAgo(r.updated_at || r.created_at);
      return { icon: meta.icon, color: meta.color, name, action, target, when, id: r.request_id || r.id };
    });
  }, [recentRequests]);

  const decideRequest = async (req, decision) => {
    const id = req?.request_id || req?.id;
    if (!id) return;
    setApprovalBusy(true);
    try {
      await approveLeaveRequest(id, { decision });
      setPendingApprovals((rows) => rows.filter((r) => (r.request_id || r.id) !== id));
    } catch {
      /* Dedicated Approve tab surfaces full error UX */
    } finally {
      setApprovalBusy(false);
    }
  };

  const quickActions = [
    // Apply Leave tile hidden for now — Apply Leave is in the sidebar and the page header.
    // canApply && { icon: Plus, label: "Apply Leave", variant: "mark", onClick: () => onNavigate("apply") },
    { icon: Wallet, label: "Leave Balances", variant: "teal", onClick: () => onNavigate("balances") },
    { icon: PartyPopper, label: "Holiday List", variant: "holiday", onClick: () => onNavigate("holidays") },
    isApprover && { icon: CheckCheck, label: `Approvals (${pendingApprovals.length})`, variant: "upload", onClick: () => onNavigate("approval") },
    isApprover && { icon: BarChart3, label: "Leave Analytics", variant: "calendar", onClick: () => onNavigate("analytics") },
    { icon: CalendarDays, label: "Leave Calendar", variant: "sky", onClick: () => showComingSoon("Leave Calendar") },
    { icon: Download, label: "Download History", variant: "mark", onClick: () => showComingSoon("Download History") },
    // Team Calendar — hidden for now.
    // { icon: Users, label: "Team Calendar", variant: "red", onClick: () => showComingSoon("Team Calendar") },
  ].filter(Boolean);

  const handleApprove = (req) => decideRequest(req, "approved");
  const handleReject = (req) => decideRequest(req, "rejected");

  return (
    <div className="lv-att-stack">
      {/* Coming Soon Popup */}
      {comingSoonToast && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => setComingSoonToast(null)}>
          <div className="bg-white rounded-2xl shadow-[0_16px_48px_rgba(15,23,42,0.2)] border border-slate-200 p-6 max-w-xs w-full mx-4 text-center animate-[scaleIn_0.2s_ease]" onClick={(e) => e.stopPropagation()}>
            <div className="w-12 h-12 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-3">
              <Clock size={22} className="text-amber-500" />
            </div>
            <p className="text-[16px] font-bold text-slate-900 mb-1">{comingSoonToast}</p>
            <p className="text-[13px] text-slate-500 mb-4">This feature is coming soon. Stay tuned!</p>
            <button type="button" onClick={() => setComingSoonToast(null)} className="lv-att-btn-primary">
              Got it
            </button>
          </div>
        </div>
      )}

      {/* QUICK STATS */}
      <section>
        <LeaveKpiGrid>
          <LeaveKpi tone="amber" label="Pending Requests" value={totalPending} hint="Awaiting a decision" />
          <LeaveKpi tone="green" label="Approved This Month" value={totalApproved} hint="Approved leave requests" />
          <LeaveKpi tone="red" label="Rejected" value={totalRejected} hint="Declined leave requests" />
          <LeaveKpi
            tone="indigo"
            label="Your Queue"
            value={actionableCount ?? 0}
            hint={actionableCount ? "Needs your action" : "All clear"}
          />
        </LeaveKpiGrid>
      </section>

      {/* MY LEAVE BALANCE + UTILIZATION */}
      {selfService || balances.length ? (
        <section>
          <SectionHeader
            icon={Wallet}
            eyebrow="My balance"
            title="My Leave Balance"
            sub={`Year ${year} · Live entitlements and consumption`}
            action={
              <button
                type="button"
                onClick={() => onNavigate("balances")}
                className="lv-att-link"
              >
                See details <ChevronRight size={13} />
              </button>
            }
          />
          {balances.length === 0 ? (
            <Card>
              <EmptyState
                icon={Wallet}
                title="No balance information yet"
                hint="Balances are assigned automatically from policy rules. If yours are missing, ask HR to run auto-allocation in Leave Admin."
                action={
                  <button
                    type="button"
                    onClick={() => onNavigate("balances")}
                    className="lv-att-btn-primary"
                  >
                    Open Balances
                    <ArrowUpRight size={14} />
                  </button>
                }
              />
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {balances.slice(0, 4).map((b) => {
                const { allocated, used, balance } = normalizeBalance(b);
                return (
                  <BalanceCard
                    key={b.leave_type_id || b.id || b.leave_type_code}
                    type={b.leave_type_name || b.leave_type_code || "Leave"}
                    allocated={allocated || balance + used}
                    used={used}
                    balance={balance}
                  />
                );
              })}
            </div>
          )}
          {balances.length > 0 ? (
            <div className="mt-3">
              <Card>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="att-mvp-kpi-label">Leave Utilization</p>
                    <p className="text-[26px] font-extrabold text-slate-900 tabular-nums leading-none mt-1">
                      {utilization}<span className="text-slate-400 text-lg">%</span>
                    </p>
                    <p className="text-[12px] text-slate-500 mt-1">{totalUsed} of {totalAllocated} days used this year</p>
                  </div>
                  <div className="flex-1 min-w-[200px] max-w-[420px]">
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{
                          width: `${utilization}%`,
                          background: "#0047d6",
                        }}
                      />
                    </div>
                    <div className="flex justify-between mt-1 text-[10px] font-bold text-slate-400">
                      <span>0%</span><span>25%</span><span>50%</span><span>75%</span><span>100%</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* QUICK ACTIONS */}
      <section>
        <SectionHeader
          icon={Sparkles}
          eyebrow="Shortcuts"
          title="Quick Actions"
        />
        <div
          className="att-mvp-dashboard-actions lv-att-actions"
          style={{ "--lv-qa-cols": quickActionCols(quickActions.length) }}
        >
          {quickActions.map((a) => (
            <LeaveQuickAction key={a.label} {...a} />
          ))}
        </div>
      </section>

      {/* TWO COLUMN: Upcoming + Approvals */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Upcoming Leave Timeline */}
        <Card className="lg:col-span-1" padded={false}>
          <CardHead icon={CalendarDays} title="Upcoming Leave" right={<Pill tone="blue">{upcomingMine.length}</Pill>} />
          <div className="p-3">
            {upcomingMine.length === 0 ? (
              <EmptyState
                icon={Plane}
                title="No upcoming leave"
                hint={canApply ? "Plan a break — your future self will thank you." : "No leave scheduled."}
                action={
                  canApply ? (
                    <button type="button" onClick={() => onNavigate("apply")} className="lv-att-btn-primary">
                      <Plus size={14} />
                      Apply Your First Leave
                    </button>
                  ) : null
                }
              />
            ) : (
              <div className="divide-y divide-slate-100">
                {upcomingMine.map((r) => (
                  <TimelineRow
                    key={r.request_id || r.id || `${r.start_date}-${r.leave_type_code}`}
                    type={r.leave_type_name || r.leave_type_code || "Leave"}
                    dates={`${fmtMonthDay(r.start_date)} → ${fmtMonthDay(r.end_date)}`}
                    status={(r.status || "pending").toLowerCase()}
                    days={Number(r.total_days || r.days || 1)}
                    reason={r.reason}
                  />
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Pending Approvals */}
        {isApprover ? (
          <Card className="lg:col-span-2" padded={false}>
            <CardHead
              icon={CheckCircle}
              title="Pending Approvals"
              right={
                <div className="flex items-center gap-2">
                  <Pill tone="amber">{pendingApprovals.length} awaiting</Pill>
                  <button type="button" onClick={() => onNavigate("approval")} className="lv-att-link">
                    View all <ChevronRight size={13} />
                  </button>
                </div>
              }
            />
            <div className="p-4">
              {pendingApprovals.length === 0 ? (
                <EmptyState
                  icon={CheckCheck}
                  title="You're all caught up"
                  hint="No leave requests waiting on you. Enjoy the calm."
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {pendingApprovals.slice(0, 4).map((req) => (
                    <ApprovalCard
                      key={req.request_id || req.id}
                      req={req}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      busy={approvalBusy}
                    />
                  ))}
                </div>
              )}
            </div>
          </Card>
        ) : (
          /* For non-approvers, show announcements in this slot */
          <Card className="lg:col-span-2 overflow-hidden" padded={false}>
            <CardHead icon={Megaphone} title="Announcements from HR" />
            <ul className="divide-y divide-slate-100">
              <li className="p-4 hover:bg-slate-50 flex gap-3">
                <Bell size={16} className="shrink-0 text-amber-500 mt-0.5" />
                <div>
                  <p className="text-[13px] font-bold text-slate-900">Carry-forward reminder</p>
                  <p className="text-[12px] text-slate-600">Unused earned leave above 30 days will lapse on March 31.</p>
                </div>
              </li>
              <li className="p-4 hover:bg-slate-50 flex gap-3">
                <PartyPopper size={16} className="shrink-0 text-rose-500 mt-0.5" />
                <div>
                  <p className="text-[13px] font-bold text-slate-900">Upcoming holidays</p>
                  <p className="text-[12px] text-slate-600">Plan ahead — 4 public holidays in the next 30 days.</p>
                </div>
              </li>
              <li className="p-4 hover:bg-slate-50 flex gap-3">
                <BookOpen size={16} className="shrink-0 text-emerald-500 mt-0.5" />
                <div>
                  <p className="text-[13px] font-bold text-slate-900">Policy update</p>
                  <p className="text-[12px] text-slate-600">Bereavement leave policy refreshed — please review.</p>
                </div>
              </li>
            </ul>
          </Card>
        )}
      </section>

      {/* TEAM AVAILABILITY — hidden for now (commented out on request).
       -- TEAM AVAILABILITY -- 
      <section>
        <SectionHeader
          icon={Users}
          eyebrow="Workforce"
          title="Team Availability — Today"
          sub={`${fmtDateLong(new Date())}`}
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "On Leave", icon: Plane, color: T.warning, value: onLeaveToday.length },
            { label: "Working", icon: CheckCheck, color: T.success, value: Math.max(0, 28 - onLeaveToday.length) },
            { label: "Remote / WFH", icon: Home, color: T.secondary, value: 4 },
            { label: "Business Travel", icon: MapPin, color: T.purple, value: 1 },
          ].map((s) => (
            <Card key={s.label} padded={false} className="p-4 group">
              <div className="flex items-center justify-between">
                <div
                  className="w-10 h-10 rounded-2xl flex items-center justify-center text-white shadow group-hover:scale-110 transition"
                  style={{ background: `linear-gradient(135deg, ${s.color}, ${s.color}cc)` }}
                >
                  <s.icon size={16} />
                </div>
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Today</span>
              </div>
              <p className="text-[26px] font-extrabold text-slate-900 mt-3 tabular-nums leading-none">{s.value}</p>
              <p className="text-[12px] text-slate-500 font-semibold mt-1">{s.label}</p>
            </Card>
          ))}
        </div>

        {onLeaveToday.length > 0 ? (
          <Card padded={false} className="mt-3 p-4">
            <p className="text-[12px] font-bold text-slate-600 mb-2">Who's on leave</p>
            <div className="flex flex-wrap gap-2">
              {onLeaveToday.slice(0, 12).map((r) => (
                <div key={r.request_id || r.id} className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full bg-slate-50 border border-slate-200 hover:bg-slate-100 transition">
                  <Avatar name={r.employee_name || r.employee_code || "?"} size={22} />
                  <span className="text-[12px] font-semibold text-slate-700">{r.employee_name || r.employee_code}</span>
                  <span className="text-[10px] text-slate-500">until {fmtMonthDay(r.end_date)}</span>
                </div>
              ))}
            </div>
          </Card>
        ) : null}
      </section>

      */}

      {/* COMPANY HOLIDAYS */}
      <section>
        <SectionHeader
          icon={PartyPopper}
          eyebrow="Calendar"
          title="Company Holidays"
          sub="Upcoming public and optional holidays."
          action={
            <button
              type="button"
              onClick={() => onNavigate("holidays")}
              className="lv-att-link"
            >
              View all <ChevronRight size={13} />
            </button>
          }
        />
        {upcomingHolidays.length === 0 ? (
          <Card>
            <EmptyState
              icon={PartyPopper}
              title="No upcoming holidays loaded"
              hint="HR can publish the year's holiday calendar from the Holiday master."
            />
          </Card>
        ) : (
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
            {upcomingHolidays.map((h, i) => (
              <HolidayChip
                key={h.holiday_id || h.id || `${h.holiday_date}-${i}`}
                name={h.name || h.holiday_name || "Holiday"}
                date={h.holiday_date || h.date}
                isOptional={Boolean(h.is_optional)}
                idx={i}
              />
            ))}
          </div>
        )}
      </section>

      {/* LEAVE ANALYTICS — hidden for now (commented out on request).
       -- ANALYTICS -- 
      <section>
        <SectionHeader
          icon={BarChart3}
          eyebrow="Insights"
          title="Leave Analytics"
          sub="How your organisation uses leave."
          action={
            <button
              type="button"
              onClick={() => onNavigate("analytics")}
              className="lv-att-link"
            >
              Open analytics <ChevronRight size={13} />
            </button>
          }
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
           -- Monthly trend -- 
          <Card padded={false} className="lg:col-span-2 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-slate-500">Last 12 months</p>
                <h4 className="text-[15px] font-bold text-slate-900">Monthly leave trend</h4>
              </div>
              <Pill tone="blue" icon={TrendingUp}>+8% YoY</Pill>
            </div>
            <div className="h-32">
              <MiniBars values={monthlyTrend} color={T.primary} height={128} gap={6} />
            </div>
            <div className="flex justify-between mt-2 text-[10px] font-bold text-slate-400">
              {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m) => (
                <span key={m}>{m}</span>
              ))}
            </div>
          </Card>

           -- Distribution donut -- 
          <Card padded={false} className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.12em] font-bold text-slate-500">By type</p>
                <h4 className="text-[15px] font-bold text-slate-900">Distribution</h4>
              </div>
            </div>
            <div className="flex items-center justify-center mb-3">
              <div className="relative">
                <Donut segments={typeDistribution} size={150} stroke={20} />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Total used</p>
                  <p className="text-xl font-extrabold text-slate-900 tabular-nums">
                    {typeDistribution.reduce((s, x) => s + x.value, 0)}
                  </p>
                </div>
              </div>
            </div>
            <ul className="space-y-1.5">
              {typeDistribution.slice(0, 4).map((seg) => (
                <li key={seg.label} className="flex items-center justify-between text-[12px]">
                  <span className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-sm" style={{ background: seg.color }} />
                    <span className="text-slate-700 font-semibold">{seg.label}</span>
                  </span>
                  <span className="text-slate-500 tabular-nums font-bold">{seg.value}d</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </section>

      */}

      {/* RECENT ACTIVITY + ANNOUNCEMENTS */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card padded={false} className="lg:col-span-2">
          <CardHead icon={Activity} title="Recent Updates" right={<Pill tone="slate">Live</Pill>} />
          <div className="divide-y divide-slate-100 px-4 py-1">
            {recentActivity.length > 0 ? (
              recentActivity.map((item) => (
                <ActivityRow
                  key={item.id}
                  icon={item.icon}
                  color={item.color}
                  name={item.name}
                  action={item.action}
                  target={item.target}
                  when={item.when}
                />
              ))
            ) : (
              <div className="py-8 text-center">
                <Activity size={28} className="mx-auto text-slate-300 mb-2" />
                <p className="text-[13px] text-slate-400">No recent leave activity yet.</p>
              </div>
            )}
          </div>
        </Card>

        <Card padded={false} className="overflow-hidden">
          <CardHead icon={Megaphone} title="Announcements" />
          <div className="space-y-2.5 p-4">
            <button type="button" onClick={() => onNavigate("holidays")} className="w-full text-left rounded-xl p-3 bg-gradient-to-br from-rose-50 to-pink-50 border border-rose-200 hover:shadow-md transition">
              <p className="text-[12px] font-bold text-rose-900 inline-flex items-center gap-1"><PartyPopper size={12}/> Upcoming Holidays</p>
              <p className="text-[11px] text-rose-700/80 mt-0.5">
                {upcomingHolidays.length > 0
                  ? `${upcomingHolidays.length} upcoming holiday${upcomingHolidays.length !== 1 ? "s" : ""} this year. Plan your time off.`
                  : "No upcoming holidays scheduled."}
              </p>
            </button>
            {isApprover && pendingApprovals.length > 0 && (
              <button type="button" onClick={() => onNavigate("approval")} className="w-full text-left rounded-xl p-3 bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 hover:shadow-md transition">
                <p className="text-[12px] font-bold text-emerald-900 inline-flex items-center gap-1"><CheckCircle size={12}/> Pending Approvals</p>
                <p className="text-[11px] text-emerald-700/80 mt-0.5">{pendingApprovals.length} leave request{pendingApprovals.length !== 1 ? "s" : ""} awaiting your review.</p>
              </button>
            )}
            <button type="button" onClick={() => onNavigate("balances")} className="w-full text-left rounded-xl p-3 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 hover:shadow-md transition">
              <p className="text-[12px] font-bold text-amber-900 inline-flex items-center gap-1"><Wallet size={12}/> Leave Balances</p>
              <p className="text-[11px] text-amber-700/80 mt-0.5">
                {totalAllocated > 0
                  ? `${utilization}% of allocated leave used across the team.`
                  : "View and manage employee leave balances."}
              </p>
            </button>
          </div>
        </Card>
      </section>
    </div>
  );
}

/* ============================================================
   PAGE WRAPPER
============================================================ */

export default function LeaveDashboard() {
  const { role, pageAccess } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { orgId } = useLeaveOrg();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const outletCtx = useOutletContext() || {};
  const setOutletTab = outletCtx.setTab;
  const activeTab = outletCtx.activeTab || searchParams.get("tab") || "dashboard";
  const actionableCount = outletCtx.actionableCount ?? null;

  const visibleSections = useMemo(() => {
    const keys = pageAccess?.page_keys || [];
    return ALL_SECTIONS.filter((s) => sectionAllowed(s, role, keys));
  }, [role, pageAccess?.page_keys]);

  useEffect(() => {
    if (!orgId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchLeaveReportSummary({ organisation_id: orgId })
      .then((summary) => setReport(summary))
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    if (activeTab === "comp-off") navigate("/leave/comp-off");
  }, [activeTab, navigate]);

  // Super Admin sees Assign Leave only; every other role sees Apply Leave only.
  useEffect(() => {
    if (!role || activeTab === "dashboard" || activeTab === "comp-off") return;
    if (!ALL_SECTIONS.some((s) => s.id === activeTab)) return;
    if (visibleSections.some((s) => s.id === activeTab)) return;
    if (setOutletTab) setOutletTab("dashboard");
    else setSearchParams({}, { replace: true });
  }, [role, activeTab, visibleSections, setOutletTab, setSearchParams]);

  const setTab = (tab) => {
    if (tab === "holidays") {
      navigate("/holiday-calendar");
      return;
    }
    if (tab === "comp-off") {
      navigate("/leave/comp-off");
      return;
    }
    if (outletCtx.setTab) {
      outletCtx.setTab(tab);
      return;
    }
    if (tab === "dashboard") setSearchParams({});
    else setSearchParams({ tab });
  };

  const renderContent = () => {
    if (activeTab === "dashboard") {
      if (loading) return <BrixigoPageLoader compact message="Loading your leave dashboard…" />;
      return (
        <DashboardOverview
          report={report}
          actionableCount={actionableCount}
          sections={visibleSections}
          onNavigate={setTab}
        />
      );
    }
    // A tab hidden for this role (e.g. typed into the address bar) is not rendered.
    if (role && !visibleSections.some((s) => s.id === activeTab)) {
      return (
        <div className="lv-panel">
          <p className="text-sm text-slate-600 p-6">You do not have access to this page.</p>
        </div>
      );
    }
    if (activeTab === "comp-off") {
      return <BrixigoPageLoader compact message="Opening Comp Off…" />;
    }
    const panels = {
      apply: <LvLeaveApply />,
      assign: <LvLeaveAssign />,
      balances: <LvLeaveBalances />,
      approval: <LvLeaveApproval />,
      analytics: <LvLeaveAnalytics />,
    };
    return <div className="lv-panel">{panels[activeTab]}</div>;
  };

  const TAB_META = {
    dashboard: { title: "Leave Dashboard", sub: "Overview of leave activity across your organisation." },
    apply: { title: "Apply Leave", sub: "Apply for leave and track its approval status." },
    assign: { title: "Assign Leave", sub: "Assign or record leave for an employee." },
    balances: { title: "Balances", sub: "View and manage employee leave balances, accruals and allocations." },
    approval: { title: "Approvals", sub: "Review and act on pending leave requests from your team." },
    analytics: { title: "Analytics", sub: "Insights and trends across leave usage, types and departments." },
  };
  const meta = TAB_META[activeTab] || TAB_META.dashboard;

  return (
    <div className="att-mvp att-mvp-dashboard-page bw-page lv-att">
      {/* Breadcrumb navigation instead of a title banner (dashboard and every section page). */}
      <div className="lv-breadcrumb-row">
        <nav className="lv-breadcrumbs" aria-label="Breadcrumb">
          {activeTab === "dashboard" ? (
            <span className="lv-breadcrumb-root">Leave</span>
          ) : (
            <button type="button" className="lv-breadcrumb-link" onClick={() => setTab("dashboard")}>
              Leave
            </button>
          )}
          <ChevronRight size={15} className="lv-breadcrumb-sep" aria-hidden />
          <span className="lv-breadcrumb-current" aria-current="page">
            {activeTab === "dashboard" ? "Dashboard" : meta.title}
          </span>
        </nav>
        {/* Moved from the old banner: quick "Apply Leave" for managers and employees. */}
        {activeTab === "dashboard" && !hasRole(role, [R.ADMIN, R.HR]) && (
          <button type="button" onClick={() => setTab("apply")} className="lv-att-btn-primary">
            <Plus size={15} />
            Apply Leave
          </button>
        )}
      </div>

      {/* Admin/HR/Manager navigate leave sections from the sidebar; employees keep in-page tabs. */}
      {!hasRole(role, [R.ADMIN, R.HR, R.MANAGER]) && (
        <div className="lv-tabs-wrap" role="tablist" aria-label="Leave module sections">
          <div className="lv-tabs">
            {visibleSections.map((section) => {
              const Icon = section.icon;
              const selected = activeTab === section.id;
              return (
                <button
                  key={section.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={`lv-tab lv-tab--${section.id}${selected ? " lv-tab--active" : ""}`}
                  onClick={() => setTab(section.id)}
                >
                  <Icon className="lv-tab-icon" size={14} strokeWidth={2.25} aria-hidden />
                  <span>{section.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {renderContent()}
    </div>
  );
}
